import '@banno/jha-wc/src/forms/dynamic/jha-dynamic-form/jha-dynamic-form';
import '@banno/jha-wc/src/icons/jha-icon-information.js';
import '@banno/jha-wc/src/icons/jha-icon-warning-filled.js';
import '@banno/jha-wc/src/jha-tooltip/jha-tooltip.js';
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import '@jack-henry/jh-icons/icons-wc/icon-user-plus.js';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import '@treasury/domain-components/company-users/overlays/drawers/copy-user-drawer/copy-user-drawer';
import '@treasury/domain-components/ip-and-time-access/ip-and-time-access.component';
import { CompanyUserService } from '@treasury/domain/company/user/company-user.service';
import {
    CompanyUserInformation,
    CompanyUserProductFeatureSummary,
    CompanyUserState,
} from '@treasury/domain/company/user/entities';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/backoffice/tm-bo-nav-back';
import '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/jh-ui';
import '@treasury/presentation/components/jh-ui/tm-button';
import '@treasury/presentation/components/jha-wc';
import '@treasury/presentation/components/tm-card';
import '@treasury/presentation/components/tm-email-field';
import '@treasury/presentation/components/tm-phone-field';
import '@treasury/presentation/components/tm-text-field';
import '@treasury/presentation/components/tm-wizard';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import {
    SideDrawerButton,
    SideDrawerButtonPosture,
    TmSideDrawerService,
} from '@treasury/presentation/services/side-drawer';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../commands/users/company-users/approveOrReject/approve-or-reject-drawer/approve-or-reject.component';
import { ModeType } from './company-user.types';
// eslint-disable-next-line no-duplicate-imports
import { Decision } from '../commands/users/company-users/approveOrReject/approve-or-reject-drawer/approve-or-reject.component';
import { ApproveOrRejectCompanyUserCommand } from '../commands/users/company-users/approveOrReject/approveOrReject.cmd';
import {
    DiscardChangesConfirmation,
    DiscardChangesConfirmationData,
} from './overlays/discard-changes-drawer/discard-changes.component';
import './partials/company-user-account-access/company-user-account-access.component';
import './partials/company-user-information.component';
import './partials/company-user-ip-and-time-access.component';
import './partials/company-user-limits-and-entitlements/company-user-limits-and-entitlements.component';

export const tagName = 'tm-company-user';

@customElement(tagName)
export class CreateCompanyUser extends TmBaseComponent {
    @property({ type: Number })
    public companyId?: number;

    @state()
    private userId?: number;

    @state()
    private isLoading = true;

    @DeepReactive()
    private userInfo = new CompanyUserInformation();

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly approveOrRejectCommand: ApproveOrRejectCompanyUserCommand;

    @state()
    private mode: ModeType = ModeType.Edit;

    @state()
    private activeTabIndex = 0;

    @InjectProperty()
    declare private readonly drawerService: TmSideDrawerService;

    @InjectProperty()
    declare private readonly navigationService: NavigationService;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare private readonly notificationService: NotificationService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @InjectProperty()
    declare private readonly sideDrawerService: TmSideDrawerService;

    /**
     * Whether the user is in compare mode.
     * This is to be used to show the unsubmitted or unapproved changes.
     */
    @state()
    private compareMode = false;

    @state()
    private userState?: CompanyUserState;

    @state()
    private productFeatureSummary?: CompanyUserProductFeatureSummary;

    @state()
    private isSaving = false;

    private get isBoApp() {
        return this.config.app === AppType.BackOffice;
    }

    private get canSubmit() {
        return (
            !this.userState?.isPendingApproval &&
            this.userState?.hasPendingChanges &&
            this.canAddOrEditUser
        );
    }

    private get canAddOrEditUser() {
        return true;
        // TODO: Add entitlements check

        //return this.entitlementsService.hasEntitlement('Add User') || this.entitlementsService.hasEntitlement('Edit User');
    }

    private get isReadOnly() {
        return this.userState?.isPendingApproval || !this.canAddOrEditUser;
    }

    public render() {
        return html`<tm-jh-card
            class=${classMap({
                'm-8': this.isBoApp,
            })}
        >
            <!-- ${this.renderUserStateVisualizer()} -->
            ${this.renderContent()}
        </tm-jh-card>`;
    }

    protected async firstUpdated() {
        const { params } = await this.navigationService.getRouteData<{
            userId: string;
            companyId: string;
        }>();
        this.userId = parseInt(params.userId);
        if (isNaN(this.userId)) {
            this.userId = undefined;
        }
        this.companyId = parseInt(params.companyId);

        // If there no user id we are creating a new user
        if (!this.userId) {
            this.mode = ModeType.Create;
            this.isLoading = false;
            return;
        }

        // (Else) There is a user id so we are editing
        this.mode = ModeType.Edit;
        await this.hydrate();
        this.isLoading = false;
    }

    private async hydrate() {
        try {
            if (this.isBoApp && !this.companyId) {
                throw new Error('Company ID is required');
            }
            if (!this.userId) {
                throw new Error('User ID is required');
            }
            await this.hydrateCompanyUserInformation(this.userId, this.companyId);
            await this.hydrateUserState();
            await this.hydrateProductFeatureSummary();
        } catch {
            this.toastService.show(
                'Error hydrating company user state',
                TmToastPosture.Danger,
                true
            );
        }
    }

    private async rehydrate() {
        try {
            await this.hydrateUserState();
            await this.hydrateProductFeatureSummary();
        } catch {
            this.toastService.show('Error rehydrating company user', TmToastPosture.Danger, true);
        }
    }

    private renderUserStateVisualizer() {
        return this.userState
            ? html`
                  <div>New: ${this.userState?.isNew}</div>
                  <div>Draft: ${this.userState?.isDraft}</div>
                  <div>Pending Approval: ${this.userState?.isPendingApproval}</div>
                  <div>Has Pending Changes: ${this.userState?.hasPendingChanges}</div>
                  <div>Approval Required: ${this.userState?.approvalRequired}</div>
                  <div>Has Minimum Requirements: ${this.userState?.hasMinimumRequirements}</div>
              `
            : html`<div>No user state</div>`;
    }

    private renderContent() {
        if (this.isLoading) {
            return html`<jha-progress></jha-progress> Loading User Information...`;
        }
        return html`
            ${this.companyId
                ? html`<tm-bo-nav-back
                      class="mb-5"
                      label="Users"
                      target="/company/${this.companyId}/users"
                  ></tm-bo-nav-back>`
                : nothing}
            <div class="flex justify-between gap-3 items-center mb-3">
                <h1 class="text-2xl font-bold">
                    ${this.mode === ModeType.Create ? 'Add' : 'Edit'} User
                </h1>
                ${this.renderAuditDetails()}
            </div>

            <div class="flex gap-x-10 gap-y-5 justify-between items-start flex-wrap">
                <div class="flex flex-1 flex-col gap-2">
                    ${this.renderPendingChangesNotice()} ${this.renderPendingApprovalNotice()}
                </div>
                <div class="flex flex-0 flex-col  items-end gap-2">
                    <div class="flex flex-row gap-2">
                        ${this.renderSubmissionControls()} ${this.renderDeleteUserButton()}
                        ${this.renderCompareModeControls()}
                    </div>
                    ${this.renderApprovalControls()}
                </div>
            </div>
            <tm-wizard
                ?loading=${this.isSaving}
                .wizardMode=${this.mode === ModeType.Create}
                .activeTabIndex=${this.activeTabIndex}
                @tab-change=${(e: CustomEvent) => (this.activeTabIndex = e.detail.index)}
                class="mb-8"
            >
                <tm-company-user-information
                    .companyId=${this.companyId}
                    .userId=${this.userId}
                    .userInfo=${this.userInfo}
                    ?isReadOnly=${this.isReadOnly}
                    @begin-save=${this.handleBeginSave}
                    @save=${this.handleUserInfoSave}
                ></tm-company-user-information>
                ${this.userId
                    ? html`<tm-company-user-ip-and-time-access
                              .companyId=${this.companyId}
                              .userId=${this.userId}
                              .mode=${this.mode}
                              ?isReadOnly=${this.isReadOnly}
                              @begin-save=${this.handleBeginSave}
                              @save=${this.handleSave}
                              @continue=${this.handleContinue}
                          ></tm-company-user-ip-and-time-access>
                          <tm-company-user-account-access
                              .companyId=${this.companyId}
                              .userId=${this.userId}
                              .mode=${this.mode}
                              ?isReadOnly=${this.isReadOnly}
                              @begin-save=${this.handleBeginSave}
                              @save=${this.handleSave}
                              @continue=${this.handleContinue}
                          ></tm-company-user-account-access>
                          <tm-company-user-limits-and-entitlements
                              .companyId=${this.companyId}
                              .userId=${this.userId}
                              .productFeatureSummary=${this.productFeatureSummary}
                              .mode=${this.mode}
                              .formData=${this.userInfo}
                              ?isReadOnly=${this.isReadOnly}
                              @begin-save=${this.handleBeginSave}
                              @save=${this.handleSave}
                          ></tm-company-user-limits-and-entitlements>`
                    : nothing}
            </tm-wizard>
        `;
    }

    private async hydrateCompanyUserInformation(userId: number, companyId?: number) {
        try {
            const { user } = await this.companyUserService.getCompanyUserInformation(
                userId,
                companyId
            );

            this.userInfo = user;

            this.requestUpdate();
        } catch {
            this.toastService.show(
                'Error loading company user information',
                TmToastPosture.Danger,
                true
            );
        }
    }

    private async hydrateProductFeatureSummary() {
        if (!this.userId) {
            throw new Error('User ID is required to load product feature summary');
        }
        this.productFeatureSummary =
            await this.companyUserService.getCompanyUserProductFeatureSummary(
                this.userId,
                this.companyId
            );
    }

    private async hydrateUserState() {
        try {
            if (!this.userId) {
                throw new Error('User ID is required to load company user state');
            }
            this.userState = await this.companyUserService.getCompanyUserState(
                this.userId,
                this.companyId
            );
        } catch {
            this.toastService.show('Error loading company user state', TmToastPosture.Danger, true);
        }
    }

    private renderCompareModeControls() {
        if (!this.userState?.isEnrolled) {
            return nothing;
        }
        if (!this.userState.isDraft && !this.userState.isPendingApproval) {
            return nothing;
        }

        const state = this.compareMode ? 'Hide' : 'Show';
        return html`
            ${this.userState.isDraft
                ? html`
                      <tm-jh-button
                          .label=${`${state} Unsubmitted Edits`}
                          @click=${this.toggleCompareMode}
                          .appearance=${'secondary'}
                      >
                      </tm-jh-button>
                  `
                : nothing}
            ${this.userState?.isPendingApproval
                ? html`
                      <tm-jh-button
                          @click=${this.toggleCompareMode}
                          .appearance=${'secondary'}
                          .label=${`${state} Unapproved Edits`}
                      >
                      </tm-jh-button>
                  `
                : nothing}
        `;
    }

    private renderAuditDetails() {
        if (!this.userState?.lastEditedBy || !this.userState?.lastEditedOn) {
            return nothing;
        }
        const auditLabel = this.userState?.isPendingApproval ? 'Submitted For Approval' : 'Edited';
        return html`
            <div>
                <b>${auditLabel}</b> by ${this.userState?.lastEditedBy} on
                ${this.userState?.lastEditedOn}
            </div>
        `;
    }

    private renderPendingChangesNotice() {
        if (!this.userState?.isDraft) {
            return nothing;
        }

        let label = '';
        if (this.userState?.approvalRequired) {
            label += ' for approval';
        } else if (this.userState?.isNew) {
            label += ' for enrollment';
        }

        return html`
            <div class="message flex gap-2 items-start">
                <jha-icon-warning-filled
                    class="text-orange-500 flex-0 w-6 h-6 fill-current"
                ></jha-icon-warning-filled>
                <div class="alert-body min-w-96 flex-1">
                    <strong>Note:</strong> Changes have been made to this user that have not been
                    submitted${label}. <br />
                    You may resume editing, submit the current edits${label}, or you may take the
                    option to discard all edits.
                    <a
                        class="text-blue-500 hover:underline cursor-pointer"
                        @click=${this.handleDiscardConfirmation}
                        >Discard All Edits</a
                    >
                </div>
            </div>
        `;
    }

    private renderPendingApprovalNotice() {
        if (!this.userState?.isPendingApproval) {
            return nothing;
        }
        return html`
            <div class="message flex gap-2 items-start">
                <jha-icon-warning-filled
                    class="text-orange-500 flex-0 w-6 h-6 fill-current mt-1"
                ></jha-icon-warning-filled>
                <div class="flex-1">
                    <strong>Note:</strong> This user is Pending Approval. No updates to this user
                    can be made at this time.
                </div>
            </div>
        `;
    }

    private renderApprovalControls() {
        if (!this.userState?.isPendingApproval) {
            return nothing;
        }
        return html`
            <div class="flex flex-row gap-2">
                <tm-jh-button
                    .label=${'Approve'}
                    @click=${this.handleApprove}
                    .appearance=${'primary'}
                >
                </tm-jh-button>
                <tm-jh-button
                    .label=${'Reject'}
                    @click=${this.handleReject}
                    .appearance=${'primary'}
                >
                </tm-jh-button>
            </div>
        `;
    }

    private renderSubmissionControls() {
        if (!this.userState?.hasPendingChanges || !this.canSubmit || this.isReadOnly) {
            return nothing;
        }

        let label = 'Submit';
        if (this.userState?.approvalRequired) {
            label = 'Submit for Approval';
        } else if (this.userState?.isNew) {
            label = 'Submit for Enrollment';
        }

        return html`
            <div class="flex flex-row gap-1 items-start">
                <jha-tooltip
                    left
                    text="To enroll user, the user must be granted access to at least one account on the account access or established as Super user or Admin user."
                >
                    <jha-icon-information
                        class="text-blue-500 fill-current cursor-pointer p-0"
                    ></jha-icon-information>
                </jha-tooltip>
                <tm-jh-button
                    .label=${label}
                    .disabled=${!this.userState?.hasMinimumRequirements}
                    .appearance=${'primary'}
                >
                </tm-jh-button>
            </div>
        `;
    }

    private renderDeleteUserButton() {
        if (this.mode === ModeType.Create) {
            return nothing;
        }
        return html`
            <tm-jh-button
                .label=${'Delete User'}
                @click=${this.handleDeleteUserConfirmation}
            ></tm-jh-button>
        `;
    }

    private toggleCompareMode() {
        this.compareMode = !this.compareMode;
        alert('Not implemented');
    }

    private handleApprove(e: CustomEvent) {
        this.handleApproveOrRejectConfirmation(e, Decision.Approve);
    }

    private handleContinue() {
        this.activeTabIndex++;
    }

    private handleReject(e: CustomEvent) {
        this.handleApproveOrRejectConfirmation(e, Decision.Reject);
    }

    private handleBeginSave() {
        this.isSaving = true;
        this.requestUpdate();
    }

    private handleSave() {
        this.isSaving = false;
        this.requestUpdate();
    }

    private async handleUserInfoSave(e: CustomEvent<{ userId: number }>) {
        this.userId = e.detail.userId;
        await this.rehydrate();
        this.activeTabIndex++;
        this.isSaving = false;
    }

    private async handleDiscardConfirmation(e: Event) {
        const switchElement = e.target as HTMLInputElement;
        switchElement.disabled = true;
        const buttons: SideDrawerButton<DiscardChangesConfirmationData>[] = [
            {
                label: 'Discard Changes',
                posture: SideDrawerButtonPosture.Primary,
                reason: DynamicComponentExitReason.Confirm,
                checkEnabled: data => {
                    if (!data) {
                        return false;
                    }
                    return data && data.comments.length > 0;
                },
            },
            {
                label: 'Cancel',
                posture: SideDrawerButtonPosture.Tertiary,
                reason: DynamicComponentExitReason.Cancel,
            },
        ];

        let comments = '';

        const result = await this.sideDrawerService.open<DiscardChangesConfirmationData>(
            setData =>
                html`<tm-bo-discard-changes-confirmation
                    /* v8 ignore next 2 */
                    @comment-change=${(e: CustomEvent) => {
                        comments = e.detail;
                        setData({ comments });
                    }}
                ></tm-bo-discard-changes-confirmation>`,
            DiscardChangesConfirmation.title,

            // Hack: We are having to cast to an unknown type because the side-drawer service
            // is not properly handling our data type. This is a temporary fix until the service
            // is updated to handle the correct data type.
            { align: 'right', buttons: buttons as SideDrawerButton<unknown>[] }
        );

        const exitReasons = [
            DynamicComponentExitReason.Cancel,
            DynamicComponentExitReason.ForceClose,
        ];

        if (exitReasons.includes(result.reason)) {
            switchElement.checked = !switchElement.checked;
            switchElement.disabled = false;
            return;
        }

        try {
            if (!this.userId) {
                throw new Error('User ID is required to discard company user changes');
            }
            const success = await this.companyUserService.discardCompanyUserChanges(
                this.userId,
                comments,
                this.companyId
            );
            if (!success) {
                throw new Error('Failed to discard company user changes');
            }

            await this.rehydrate();

            this.toastService.show(
                'Company User Changes Discarded Successfully',
                TmToastPosture.Success
            );
        } catch {
            this.toastService.show(
                'Failed to discard company user changes',
                TmToastPosture.Danger,
                true
            );
            switchElement.checked = !switchElement.checked;
        } finally {
            switchElement.disabled = false;
        }
    }

    private async handleApproveOrRejectConfirmation(e: CustomEvent, decision: Decision) {
        const userSummary = this.userState?.toCompanyUserSummary();
        if (!userSummary) {
            this.toastService.show(
                'User ID is required to approve company user',
                TmToastPosture.Danger,
                true
            );
            return;
        }
        this.approveOrRejectCommand.command(decision, this.companyId).action(userSummary);
    }

    private async handleDeleteUserConfirmation(e: Event) {
        if (!this.userId) {
            this.toastService.show(
                'User ID is required to delete a company user',
                TmToastPosture.Danger
            );
            return;
        }

        const buttonElement = e.target as HTMLButtonElement;
        buttonElement.disabled = true;

        const { reason } = await this.dialogService.open(
            html`<div>
                Are you sure you want to delete this company user? <br />
                <span class="text-red-500 font-bold">This action cannot be undone.</span>
            </div>`,
            'Delete Company User',
            {
                buttons: [
                    {
                        label: 'Yes',
                        reason: DynamicComponentExitReason.Confirm,
                        posture: TmDialogButtonPosture.Primary,
                    },
                    {
                        label: 'No',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: TmDialogButtonPosture.Secondary,
                    },
                ],
            }
        );

        if (reason !== DynamicComponentExitReason.Confirm) {
            buttonElement.disabled = false;
            return;
        }
        try {
            const success = await this.companyUserService.deleteCompanyUser(
                this.userId,
                this.companyId
            );
            if (!success) {
                throw new Error('Failed to delete company user');
            }

            this.toastService.show('Company User Deleted Successfully', TmToastPosture.Success);
            this.navigationService.navigate(`/company/${this.companyId}/users`);
        } catch {
            this.toastService.show('Failed to delete company user', TmToastPosture.Danger, true);
        } finally {
            this.requestUpdate();
            buttonElement.disabled = false;
        }
    }

    private async handleFinalSave() {
        this.navigationService.navigate(`/company/${this.companyId}/users`);
        this.notificationService.renderSuccess({
            duration: 7000,
            position: 'bottom-start',
            content: `${this.userInfo.name} has been successfully added as user.`,
        });
    }

    public static get styles() {
        return css`
            .message {
                border: 1px solid rgb(249 115 22);
                padding: 10px;
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CreateCompanyUser;
    }
}
